/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

const wsApi = process.env.REACT_APP_WS_API;

// A page hidden longer than this gets a fresh socket on return: mobile OSes
// kill suspended sockets without firing close, leaving readyState OPEN
// ("zombie") — the feed would silently freeze if we trusted readyState.
const STALE_HIDDEN_MS = 30000;

type WSContextType = {
  isConnected: boolean;
  ws: WebSocket | null;
};
const WSContext = createContext({ isConnected: false, ws: null } as WSContextType);

const WithWebSockets = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [reTryIndex, setReTryIndex] = useState<number>(0);

  // If user is trying to reconnect - we need to track down already created timeOuts
  // To avoid unnecessary tries
  const timeoutRef = useRef<null | NodeJS.Timeout>(null);
  const hiddenAtRef = useRef<number | null>(null);

  const wsToken = useSelector((store: RootState) => store.user.tokens.ws);

  // If server is dead - user internet is dead or something else
  // WS connection will be closed - but user can get internet back up
  // so we need to try to reconnect after some time until we will succeed
  const tryToReconnect = () => {
    const timeout = setTimeout(() => {
      // readyState 3 = CLOSED connection
      // If old connection is closed - we retry
      // If open we don't do anything
      // Otherwise we will have endless loop because of useEffect where we close connection
      setWs((oldWs) => {
        if (oldWs?.readyState === 3 || oldWs?.readyState === 2) {
          setReTryIndex((prev) => prev + 1);
        }
        return oldWs;
      });
    }, parseInt(process.env.REACT_APP_PING_INTERVAL || '5000'));
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = timeout;
  };

  // Rebuild or retry the connection now: a dead socket retries immediately;
  // an apparently-open one is closed (when asked), which flows through
  // onclose → tryToReconnect → fresh socket.
  const forceRefresh = (closeIfOpen: boolean) => {
    setWs((oldWs) => {
      if (!oldWs
        || oldWs.readyState === WebSocket.CLOSED
        || oldWs.readyState === WebSocket.CLOSING) {
        setReTryIndex((prev) => prev + 1);
      } else if (closeIfOpen && oldWs.readyState === WebSocket.OPEN) {
        oldWs.close();
      }
      return oldWs;
    });
  };

  // Phones suspend the page on screen lock / app switch and kill sockets
  // without a close event; network switches (WiFi↔cellular) do the same.
  // Recover on both signals instead of waiting for a close that never fires.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenFor = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0;
      hiddenAtRef.current = null;
      forceRefresh(hiddenFor > STALE_HIDDEN_MS);
    };
    const onOnline = () => forceRefresh(true);

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  useEffect(() => {
    let connection: WebSocket | null = null;

    if (wsToken) {
      connection = new WebSocket(`${wsApi}?token=${wsToken}`);

      setWs(connection);
      connection.onopen = () => {
        setIsConnected(true);
      };

      connection.onclose = () => {
        setIsConnected(false);
        tryToReconnect();
      };

      connection.onerror = () => {
        // eslint-disable-next-line no-console
        console.error('[WS] WebSocket error');
        // Force close to trigger onclose → reconnect flow
        if (connection?.readyState === WebSocket.OPEN
          || connection?.readyState === WebSocket.CONNECTING) {
          connection.close();
        }
      };

      return () => {
        // Close CONNECTING sockets too — leaving one behind on token change
        // or retry leaks the connection.
        if (connection && (connection.readyState === WebSocket.OPEN
          || connection.readyState === WebSocket.CONNECTING)) {
          connection.close();
        }
      };
    }
    return () => {};
  }, [wsToken, reTryIndex]);

  return (
    <WSContext.Provider value={{ isConnected, ws }}>
      { children }
    </WSContext.Provider>
  );
};

const useWebSockets = () => useContext(WSContext);

export default {
  WithWebSockets,
  useWebSockets,
};
